import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { XMarkIcon, CheckCircleIcon, CheckIcon } from "@heroicons/react/24/outline";
import { Controller, useForm } from "react-hook-form";
import { ICartState } from "@/app_redux/reducers/slices/auth/cart_slice";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import clsx from "clsx";
import CircleSpinnerLoader from "@/app/components/ui/Reusable/LoaderComponent/CircleSpinnerLoader";
import { useNavigate } from "react-router";
import UserPetDetailsComponent from "@/app/components/ui/Reusable/UserPetDetailsComponent";
import { useSingleBookingDetailsAPI } from "@/app/hooks/api_hooks/bookings/useSingleBookingDetailsAPI";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { useBookingExemptiopnDetailsAPI } from "@/app/hooks/api_hooks/bookings/useBookingExemptiopnDetailsAPI";
import { useBookingExemptiopAPI } from "@/app/hooks/api_hooks/bookings/useBookingExemptiopAPI";

type Props = {
  open: boolean;
  setOpen: (value: boolean) => void;
  bookingDetails?: any;
  fetchAppointmentData?: (reason_of_deletion?: string) => void;
  source?: "day_closing_module";
  exemptionIntent?: string;
};

const ExamptionModalComponent: React.FC<Props> = ({
  open,
  setOpen,
  bookingDetails,
  fetchAppointmentData,
  source,
  exemptionIntent,
}) => {
  const { getRouteKey } = useRouter();
  const cancelButtonRef = useRef(null);

  const { refundRequestFilter } = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id")).routeState.state as ICartState;
  const { mappedClinicName } = useAppSelector((state) => state.root.UIStates);

  const { customerDetails } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState
    .state as ICustomerProfileState;

  const fetchBookingDetails = useSingleBookingDetailsAPI();
  const [cartLevelBookingDetails, setCartLevelBookingDetails] = useState(null);
  const [exemptionReasonL2, setExemptionReasonL2] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [successData, setSuccessData] = useState<any>(null);
  const [isExemptionEligible, setIsExemptionEligible] = useState(true);
  const [isExemptionRequiredForDeletion, setIsExemptionRequiredForDeletion] = useState(true);
  const [removeReason, setRemoveReason] = useState("");

  const { execute: fecthExcemptionReason, exemptionReason, exemptionMessage } = useBookingExemptiopnDetailsAPI();
  const { execute: createExemption } = useBookingExemptiopAPI();

  const formHook = useForm<any>({
    mode: "onChange",
    defaultValues: {
      refund_type: "wallet",
    },
  });

  const {
    formState: { errors },
  } = useForm();

  const targetBookingId = typeof bookingDetails === "string" ? bookingDetails : bookingDetails?.booking_id;
  const isDeleteCartItemFlow = source === "day_closing_module";
  const effectiveExemptionIntent = isDeleteCartItemFlow ? "delete_cart_item" : exemptionIntent;
  const isDeleteCartItemIntent = (effectiveExemptionIntent ?? "delete_cart_item") === "delete_cart_item";
  const targetCartItemUniqueId =
    (typeof bookingDetails === "object" ? bookingDetails?.unique_row_id : undefined) ||
    cartLevelBookingDetails?.unique_row_id;
  const shouldSendIntent = Boolean(effectiveExemptionIntent);
  const shouldAttachCartItemUniqueId = effectiveExemptionIntent === "delete_cart_item" && Boolean(targetCartItemUniqueId);
  const reason1 = formHook.watch("reason_l1");
  const reason2 = formHook.watch("reason_l2");
  const otherReason = formHook.watch("otherReason") || "";

  useEffect(() => {
    if (!open || !targetBookingId) return;
    fetchBookingDetails(targetBookingId).then((response) => {
      setCartLevelBookingDetails(response);
    });
  }, [open, targetBookingId]);

  useEffect(() => {
    if (refundRequestFilter) {
      formHook.setValue("filters", refundRequestFilter);
    }
  }, [refundRequestFilter]);

  useEffect(() => {
    if (!open) return;
    formHook.reset({
      refund_type: "wallet",
      reason_l1: "",
      reason_l2: "",
      otherReason: "",
      filters: refundRequestFilter ?? undefined,
    });
    setExemptionReasonL2([]);
    setSuccessData(null);
    setRemoveReason("");
  }, [open, refundRequestFilter]);

  /* Redux States Ends */

  /* Redux States Ends */

  const handelViewOpenFile = () => {
    const url = `https://docs.google.com/forms/d/e/1FAIpQLSchOkufgQm9dC1hP_drkFBySoBbjyvV2DEPJqVOxVCT6nPEPA/viewform?usp=pp_url&entry.683484130=${bookingDetails?.user_details?.name}&entry.1914162699=${bookingDetails?.pet_details?.name}&entry.881427304=${bookingDetails?.user_details?.id}&entry.844793845=${bookingDetails?.user_details?.mobile}&entry.223007323=${mappedClinicName}&entry.383927314=${bookingDetails?.is_member !== "Non-Member" ? "Member" : "Non Member"}&entry.1555006011=${bookingDetails?.service_details?.name}`;
    window.open(url, "_blank");
  };

  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  // ✅ read query params
  const queryClinics = queryParams.get("clinic_ids");
  const queryShift = queryParams.get("shift") ?? "day";
  const processId = queryParams.get("closing_process");
  const isClinicView = queryParams.get("is_clinic");
  const city_id = queryParams.get("city_id");

  const handelClick = () => {
    if (isDeleteCartItemIntent && !isExemptionRequiredForDeletion) {
      setOpen(false);
      fetchAppointmentData?.();
      return;
    }

    setIsLoading(true);
    const payload = {
      booking_id: targetBookingId,
      exemption_l1: formHook.watch("reason_l1"),
      exemption_l2: formHook.watch("reason_l2"),
      exemption_details: formHook.watch("otherReason"),
      ...(shouldSendIntent && effectiveExemptionIntent ? { intent: effectiveExemptionIntent } : {}),
      ...(shouldAttachCartItemUniqueId ? { cart_item_unique_id: targetCartItemUniqueId } : {}),
    };
    createExemption(payload)
      .then(({ status, message }) => {
        setIsLoading(false);
        if (status) {
          if (message?.next_steps) {
            setSuccessData(message);
          } else {
            processId &&
              navigate(
                `/app/closing/day-closing?closing_process=${processId}&city_id=${city_id}&clinic_ids=${queryClinics}&is_clinic=${isClinicView}&shift=${queryShift}&t=${Date.now()}`,
              );
            setOpen(false);
            fetchAppointmentData?.();
          }
        } else {
          // Fallback or error handling if status is false but no error thrown
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    const eligibleExemptions = exemptionReason?.eligible_exemptions ?? [];
    if (reason1) {
      const reasonL2 = eligibleExemptions?.filter((item, _id) => item.title === reason1);
      setExemptionReasonL2(reasonL2?.length > 0 && reasonL2[0]?.reasons);
    } else {
      setExemptionReasonL2([]);
    }
  }, [reason1, exemptionReason]);

  useEffect(() => {
    if (!open || !targetBookingId) return;

    setIsExemptionEligible(true);
    setIsExemptionRequiredForDeletion(true);

    const exemptionReasonQueryParams =
      shouldSendIntent && effectiveExemptionIntent
        ? {
            intent: effectiveExemptionIntent,
            ...(shouldAttachCartItemUniqueId ? { cart_item_unique_id: targetCartItemUniqueId } : {}),
          }
        : undefined;

    fecthExcemptionReason(targetBookingId, exemptionReasonQueryParams)
      .then((res) => {
        if (!res || !res.status) {
          setIsExemptionEligible(false);
          return;
        }

        const isRequiredForDeletion = res?.data?.exemption_required_for_deletion !== false;
        const hasEligibleExemptions = (res?.data?.eligible_exemptions?.length ?? 0) > 0;
        setIsExemptionRequiredForDeletion(isRequiredForDeletion);

        if (isDeleteCartItemIntent && !isRequiredForDeletion) {
          setIsExemptionEligible(false);
          return;
        }

        if (!hasEligibleExemptions) {
          setIsExemptionEligible(false);
        }
      })
      .catch(() => {
        setIsExemptionEligible(false);
      });
  }, [
    open,
    targetBookingId,
    targetCartItemUniqueId,
    effectiveExemptionIntent,
    shouldSendIntent,
    shouldAttachCartItemUniqueId,
    isDeleteCartItemIntent,
  ]);

  const isRemoveReasonInvalid = removeReason.trim().length === 0;

  // set by the API on L2 reasons that can't be raised yet, e.g. EMR is mandatory but missing
  const reasonL2ErrorMessage: string | undefined = (exemptionReasonL2 || []).find(
    (item: any) => item.title === reason2,
  )?.error_message;

  // INVALID if ANY of these are true
  const isInvalid =
    !isExemptionEligible || !reason1 || !reason2 || Boolean(reasonL2ErrorMessage) || otherReason.trim().length < 25;

  return (
    <div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-[70] overflow-y-auto"
          initialFocus={cancelButtonRef}
          onClose={() => {
            setOpen(false);
          }}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
            </Transition.Child>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-visible shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full sm:max-w-xl">
                <div className="bg-white p-6">
                  {successData ? (
                    <div className="bg-white p-6 rounded-lg">
                      <div className="flex justify-center mb-4">
                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                          <CheckCircleIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
                        </div>
                      </div>
                      <h3 className="text-lg font-medium text-center text-gray-900 mb-4">Exemption Processed</h3>

                      {successData?.next_steps && (
                        <div className="bg-blue-50 p-3 rounded-md mb-4 border border-blue-200">
                          <p className="text-sm text-blue-800 font-semibold mb-1">Next Steps:</p>
                          <p className="text-sm text-blue-700">{successData.next_steps}</p>
                        </div>
                      )}

                      {!successData?.next_steps && (
                        <div className="space-y-4 max-h-[300px] overflow-y-auto">
                          {Object.values(successData?.actions || {})
                            .filter((action: any) => action.success)
                            .length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                  Successful Actions
                                </p>
                                <ul className="space-y-2">
                                  {Object.values(successData?.actions || {})
                                    .filter((action: any) => action.success)
                                    .map((action: any, idx) => (
                                      <li key={idx} className="flex items-center text-sm text-gray-700">
                                        <CheckIcon className="h-4 w-4 text-green-500 mr-2" />
                                        {action.action
                                          .replace(/_/g, " ")
                                          .replace(/\b\w/g, (c: string) => c.toUpperCase())}
                                      </li>
                                    ))}
                                </ul>
                              </div>
                            )}

                          {Object.values(successData?.actions || {})
                            .filter((action: any) => !action.success)
                            .length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                  Failed Actions
                                </p>
                                <ul className="space-y-2">
                                  {Object.values(successData?.actions || {})
                                    .filter((action: any) => !action.success)
                                    .map((action: any, idx) => (
                                      <li key={idx} className="flex items-center text-sm text-gray-700">
                                        <XMarkIcon className="h-4 w-4 text-red-500 mr-2" />
                                        {action.action
                                          .replace(/_/g, " ")
                                          .replace(/\b\w/g, (c: string) => c.toUpperCase())}
                                      </li>
                                    ))}
                                </ul>
                              </div>
                            )}
                        </div>
                      )}

                      <div className="mt-6">
                        <button
                          onClick={() => {
                            processId &&
                              navigate(
                                `/app/closing/day-closing?closing_process=${processId}&city_id=${city_id}&clinic_ids=${queryClinics}&is_clinic=${isClinicView}&shift=${queryShift}&t=${Date.now()}`,
                              );
                            setOpen(false);
                            fetchAppointmentData?.();
                          }}
                          className="w-full bg-black text-white py-2 rounded-md hover:bg-gray-800 transition-colors font-medium text-sm"
                        >
                          OK
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div
                        className={clsx("flex items-center mt-2", {
                          "justify-between": true,
                        })}
                      >
                        <h2 className="text-base font-medium">
                          {isExemptionRequiredForDeletion ? "Raise an exemption" : "Enter reason"}
                        </h2>

                        <span
                          onClick={() => {
                            setOpen(false);
                          }}
                        >
                          <XMarkIcon className="h-5 w-5 cursor-pointer" />
                        </span>
                      </div>
                      {isExemptionRequiredForDeletion && (
                        <UserPetDetailsComponent
                          userDetails={cartLevelBookingDetails ? customerDetails : bookingDetails?.user_details}
                          petDetails={
                            cartLevelBookingDetails ? cartLevelBookingDetails?.pet_details : bookingDetails?.pet_details
                          }
                        />
                      )}
                      <div className=" items-start py-3   w-full">
                        {isDeleteCartItemIntent && !isExemptionRequiredForDeletion ? (
                          source === "day_closing_module" ? (
                            <div className="flex flex-col gap-4">
                              <p className="text-red-500 text-sm font-medium">
                                You are removing{" "}
                                <strong>
                                  {bookingDetails?.item_name ??
                                    cartLevelBookingDetails?.service_details?.name ??
                                    bookingDetails?.service_details?.name ??
                                    "this service"}
                                </strong>{" "}
                                from cart. Please enter the reason for removing this service.
                              </p>
                              <textarea
                                rows={2}
                                value={removeReason}
                                onChange={(e) => setRemoveReason(e.target.value)}
                                placeholder="Enter your reason here"
                                className="w-full p-2 border border-gray-300 rounded-md text-sm"
                              />
                            </div>
                          ) : (
                            <div className="w-full rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">
                              Exemption is not required for deletion for this booking.
                            </div>
                          )
                        ) : !isExemptionEligible ? (
                          <div className="w-full rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                            {exemptionMessage ?? "This Appointment is not eligible for Exemption."}
                          </div>
                        ) : (
                          <div className="flex flex-col   gap-4 ">
                            {exemptionReason?.exemption_type && (
                              <div className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
                                Exemption Type: <span className="font-medium">{exemptionReason.exemption_type}</span>
                              </div>
                            )}
                            <SearchableSelectMenu
                              errorMessages={[{ message: "", type: "required" }]}
                              onSelectItem={(item) => {
                                if (item) {
                                  formHook.setValue("reason_l1", item.title);
                                }
                              }}
                              fieldError={formHook.formState.errors.reason_l1}
                              register={formHook.register("reason_l1", { required: true })}
                              selectItems={exemptionReason?.eligible_exemptions ?? []}
                              placeholder="Select L1"
                              showTooltips={true}
                              showTypedErrors={false}
                              showDropdownIcon={true}
                              defaultSelected={
                                (exemptionReason?.eligible_exemptions ?? []).filter(
                                  (oc) => oc.title === formHook.watch("reason_l1"),
                                )[0]
                              }
                              listBoxClassName="w-full"
                              className="text-gray-800 w-10 z-50"
                              containerClassName="w-full"
                            />
                            <SearchableSelectMenu
                              errorMessages={[{ message: "", type: "required" }]}
                              onSelectItem={(item) => {
                                if (item) {
                                  formHook.setValue("reason_l2", item.title);
                                }
                              }}
                              fieldError={formHook.formState.errors.reason_l2}
                              register={formHook.register("reason_l2", { required: true })}
                              selectItems={exemptionReasonL2}
                              placeholder="Select L2"
                              showTooltips={true}
                              showTypedErrors={false}
                              showDropdownIcon={true}
                              defaultSelected={
                                exemptionReasonL2?.filter((oc) => oc.title === formHook.watch("reason_l2"))[0]
                              }
                              listBoxClassName="w-full"
                              className="text-gray-800 w-10 z-50"
                              containerClassName="w-full"
                            />

                            {reasonL2ErrorMessage && (
                              <div className="w-full rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 -mt-2">
                                {reasonL2ErrorMessage}
                              </div>
                            )}

                            <Controller
                              name="otherReason"
                              control={formHook.control} // Ensure you're using the correct form instance
                              rules={
                                isExemptionRequiredForDeletion
                                  ? {
                                      required: "Enter Remarks",
                                      validate: (value) => value.trim() !== "" || "Reason cannot be blank.",
                                      minLength: {
                                        value: 25,
                                        message: "Minimum 25 characters required",
                                      },
                                    }
                                  : {}
                              }
                              render={({ field }) => (
                                <textarea
                                  {...field}
                                  rows={4}
                                  minLength={isExemptionRequiredForDeletion ? 25 : undefined}
                                  placeholder={`${formHook.watch("reason_l1") === "Duplicate Booking ID" ? "Enter other user ID" : "Enter Remarks"}`}
                                  className={`w-full p-2 border ${formHook.formState.errors.otherReason ? "border-red-400" : "border-gray-300"} rounded-md text-sm`}
                                />
                              )}
                            />

                            {formHook.formState.errors.otherReason && (
                              <p className="text-red-500 text-[10px] -mt-2">
                                {formHook.formState.errors.otherReason.message as any}
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {isDeleteCartItemIntent && !isExemptionRequiredForDeletion && source === "day_closing_module" ? (
                        <div className="gap-4 w-full">
                          <button
                            disabled={isRemoveReasonInvalid}
                            className={clsx(
                              "w-full py-2 text-sm font-medium rounded-md flex items-center justify-center gap-2",
                              {
                                "bg-[#939393] text-white": isRemoveReasonInvalid,
                                "bg-black text-white": !isRemoveReasonInvalid,
                              },
                            )}
                            onClick={() => {
                              fetchAppointmentData?.(removeReason.trim());
                              setOpen(false);
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ) : isExemptionEligible && (
                        <div className=" gap-4 w-full ">
                          <button
                            disabled={isInvalid}
                            className={clsx(
                              "w-full py-2 text-sm font-medium rounded-md flex items-center justify-center gap-2",
                              {
                                "bg-[#939393] text-white": isInvalid, // inactive state
                                "bg-black text-white": !isInvalid, // active state
                              },
                            )}
                            onClick={handelClick}
                          >
                            {isLoading ? (
                              <div>
                                <CircleSpinnerLoader h="20" w="20" />
                              </div>
                            ) : (
                              isExemptionRequiredForDeletion ? "Submit Reason" : "Delete Item"
                            )}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default ExamptionModalComponent;
